import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { MultiTextInput } from '@delmonicos/shared-design-system-react/src/components/input/multi-text-input';
import { PasswordInput } from '@delmonicos/shared-design-system-react/src/components/input/password-input';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { OcppAuthenticationLayer } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/enums/authentication/ocpp-authentication-layer.enum';
import { useUpdateChargePointFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-charge-point.form-validation';
import { UpdateChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-charge-point.model';
import { ChargePointIsLocked } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/charge-point/charge-point-is-locked.enum';
import { useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// TODO: Should be moved in SDK
const isIpWithRegex = (ip: string) => {
  const regex = new RegExp(
    '^' +
      '((25[0-5]|2[0-4]\\d|[01]?\\d\\d?)\\.)' +
      '{3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$',
  );
  return regex.test(ip);
};

const ChargePointUpdateFormStep1AuthorizedIpAddressesSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<
    Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>,
    'chargePoint.authorizedIps'
  >;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // INPUT PROPERTIES //////////////////////////////////////////////////////////

  const [value, setValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // ITEM LIST /////////////////////////////////////////////////////////////////

  const [itemList, setItemList] = useState<string[]>(field.value ?? []);

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleChange = (itemListChanged: string[]) => {
    setItemList(itemListChanged);
    field.onChange(itemListChanged);
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.XS} direction="column">
      <Text>
        {t('delmonicos-shared-sdk.outputs-charge-point.authorized-ips')}
      </Text>
      <MultiTextInput
        placeholder={t(
          'delmonicos-shared-sdk.outputs-charge-point.authorized-ips',
        )}
        inputValue={value}
        items={itemList}
        onAddItem={(item) => {
          if (item === '') {
            setErrorMessage('');
            return;
          }

          if (itemList.includes(item)) {
            setErrorMessage(
              t('$.charge-point-update.step-1.authorizations.duplicate'),
            );
            return;
          }

          if (!isIpWithRegex(item)) {
            setErrorMessage(
              t('$.charge-point-update.step-1.authorizations.invalid'),
            );
            return;
          }

          handleChange([...itemList, item]);

          setValue('');
          setErrorMessage('');
        }}
        onRemoveItem={(index) => {
          handleChange(itemList.filter((_, i) => i !== index));
        }}
        onInputChange={setValue}
        errorMessage={errorMessage || error?.message}
      />
    </Stack>
  );
};

const ChargePointUpdateFormStep1InformationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } =
    useFormContext<
      Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>
    >();
  const { chargePointIdentity, chargePointName } =
    useUpdateChargePointFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card flex={1}>
      <Stack direction="column" gap={GapEnum.L}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="info-circle"
          text={t('$.charge-point-update.step-1.information.title')}
          variant={AccentVariantEnum.Primary}
        />
        <Controller
          name="chargePoint.identity"
          control={control}
          rules={chargePointIdentity}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-charge-point.identity')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-charge-point.identity',
                )}
                required={!!chargePointIdentity.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? '' : e.target.value)
                }
              />
            );
          }}
        />
        <Controller
          name="chargePoint.name"
          control={control}
          rules={chargePointName}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-charge-point.name')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-charge-point.name',
                )}
                required={!!chargePointName.required}
                disabled={field.disabled}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? '' : e.target.value)
                }
              />
            );
          }}
        />
      </Stack>
    </Card>
  );
};

const ChargePointUpdateFormStep1IsLockedFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } =
    useFormContext<
      Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>
    >();
  const { chargePointIsLocked } = useUpdateChargePointFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card direction="column" gap={GapEnum.L}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="info-circle"
        text={t('delmonicos-shared-sdk.outputs-charge-point.is-locked')}
        variant={AccentVariantEnum.Accent1}
      />
      <Controller
        name="chargePoint.isLocked"
        control={control}
        rules={chargePointIsLocked}
        render={({ field, fieldState: { error } }) => {
          return (
            <Stack gap={GapEnum.XS} direction="column">
              <Dropdown
                label={t(
                  'delmonicos-shared-sdk.outputs-charge-point.is-locked',
                )}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-charge-point.is-locked',
                )}
                required={!!chargePointIsLocked.required}
                removable
                errorMessage={error?.message}
                {...field}
                value={
                  field.value === undefined
                    ? ''
                    : field.value
                      ? ChargePointIsLocked.TRUE
                      : ChargePointIsLocked.FALSE
                }
                onChange={(value) => {
                  if (value === undefined) {
                    field.onChange('');
                  } else {
                    field.onChange(value === ChargePointIsLocked.TRUE);
                  }
                }}>
                {Object.values(ChargePointIsLocked).map((s) => (
                  <Dropdown.Option key={s} value={s}>
                    {t(
                      `delmonicos-shared-sdk.outputs-charge-point-is-locked.${s}`,
                    )}
                  </Dropdown.Option>
                ))}
              </Dropdown>
            </Stack>
          );
        }}
      />
    </Card>
  );
};

const ChargePointUpdateFormStep1AuthenticationFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { control, watch } =
      useFormContext<
        Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>
      >();
    const {
      chargePointAuthenticationLayer,
      chargePointAuthorizedIps,
      chargePointOcppAuthenticationPassword,
    } = useUpdateChargePointFormValidation();

    // WATCHERS ////////////////////////////////////////////////////////////////

    const authenticationLayerSelected = watch(
      'chargePoint.authenticationLayer',
    );

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <Card gap={GapEnum.L} flex={1}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="log-in-02"
          text={t(
            'delmonicos-shared-sdk.outputs-charge-point.authentication-layer',
          )}
          variant={AccentVariantEnum.Primary}
        />
        <Controller
          name="chargePoint.authenticationLayer"
          control={control}
          rules={chargePointAuthenticationLayer}
          render={({ field, fieldState: { error } }) => (
            <Stack gap={GapEnum.XS} direction="column">
              <Dropdown
                label={t(
                  'delmonicos-shared-sdk.outputs-charge-point.authentication-layer',
                )}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-charge-point.authentication-layer',
                )}
                required={!!chargePointAuthenticationLayer.required}
                removable
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(value) => field.onChange(value ?? '')}>
                {Object.values(OcppAuthenticationLayer).map((s) => (
                  <Dropdown.Option key={s} value={s}>
                    {t(
                      `delmonicos-shared-sdk.enums-ocpp-authentication-layer.${s}`,
                    )}
                  </Dropdown.Option>
                ))}
              </Dropdown>
            </Stack>
          )}
        />
        {(authenticationLayerSelected ===
          OcppAuthenticationLayer.HTTP_BASIC_TLS ||
          authenticationLayerSelected ===
            OcppAuthenticationLayer.HTTP_BASIC_NO_TLS) && (
          <Controller
            name="chargePoint.ocppAuthenticationPassword"
            control={control}
            rules={chargePointOcppAuthenticationPassword}
            render={({ field, fieldState: { error } }) => {
              return (
                <PasswordInput
                  label={t(
                    'delmonicos-shared-sdk.inputs-update-charge-point.ocpp-authentication-password',
                  )}
                  placeholder={t(
                    'delmonicos-shared-sdk.inputs-update-charge-point.ocpp-authentication-password',
                  )}
                  required={!!chargePointOcppAuthenticationPassword.required}
                  errorMessage={error?.message}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value === '' ? '' : e.target.value);
                  }}
                />
              );
            }}
          />
        )}
        <Divider />
        <Controller
          name="chargePoint.authorizedIps"
          control={control}
          rules={chargePointAuthorizedIps}
          render={ChargePointUpdateFormStep1AuthorizedIpAddressesSelection}
        />
      </Card>
    );
  };

export const ChargePointUpdateFormStep1Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack
      direction="row"
      justifyContent="stretch"
      alignItems="stretch"
      flex={1}
      gap={GapEnum.XL}>
      <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.XL}>
        <ChargePointUpdateFormStep1InformationFragment />
        <ChargePointUpdateFormStep1IsLockedFragment />
      </Stack>
      <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.XL}>
        <ChargePointUpdateFormStep1AuthenticationFragment />
      </Stack>
    </Stack>
  );
};
